import React, { useEffect } from "react";

import { useEventTracker } from "@/hooks/useEventTracker";

import Carousel from "@/components/botanic/Carousel";
import ProductCard, {
  ProductCardProduct,
} from "@/components/ProductCard/ProductCard";
import TrackImpression from "@/components/TrackImpression";
import { CAROUSEL_ROUTE_MAP } from "@/pages/products";

import { Product } from "../../../custom-types/Product";
import * as GTM_EVENTS from "../lib/gtm/gtmEvents";

export type ProductsCarouselProps = {
  carouselPath:
    | (typeof CAROUSEL_ROUTE_MAP)[keyof typeof CAROUSEL_ROUTE_MAP]
    | string;
  header: React.ReactNode;
  products: Product[];
  carouselClassName?: string;
  seeMoreText?: string;
  onLoad?: () => void;
  seeMoreOnClick?: () => void;
  productCardOnClick?: () => void;
};

export const ProductsCarousel: React.FC<ProductsCarouselProps> = ({
  carouselPath,
  header,
  products,
  carouselClassName = "my-xl md:my-section",
  seeMoreText,
  onLoad,
  seeMoreOnClick,
  productCardOnClick,
}) => {
  const { publishEvent } = useEventTracker();

  const carouselSlug =
    carouselPath.split("/")[carouselPath.split("/").length - 1];

  useEffect(() => {
    if (onLoad) {
      onLoad();
    }
  }, []);

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <Carousel
      title={header}
      seeMoreHref={carouselPath}
      seeMoreText={seeMoreText}
      seeMoreOnClick={() => {
        if (seeMoreOnClick) {
          seeMoreOnClick();
        } else {
          publishEvent({
            ...GTM_EVENTS.PRODUCT.CAROUSEL_SEE_MORE_CLICK,
            label: carouselPath,
          });
        }
      }}
      className={carouselClassName}
    >
      {products.map((product) => {
        const eventLabel = carouselSlug
          ? `${carouselSlug} product card`
          : `/products/details/${product.slug}`;
        const dimensions = {
          brandId: product.brand.id,
          productId: product.id,
        };

        const productCard = {
          avgApprovedPublicReviewRating: product.avgApprovedPublicReviewRating,
          brandName: product.brand.name,
          brandSlug: product.brand.slug,
          categoryName: product.categoryName,
          cbdContent: product.cbdContent,
          cbdContentLabel: product.cbdContentLabel,
          countApprovedPublicReviews: product.countApprovedPublicReviews,

          discountLabel: product.bestOfferVariant?.deal?.discountLabel,
          displayPrice: product.bestOfferVariant?.sortPrice,
          id: product.id,
          imageUrl: product.imageUrl,
          isBestSeller: false,
          isStaffPick: false,
          name: product.name,
          normalizedQuantityLabel:
            product.bestOfferVariant?.normalizedQuantityLabel,
          offerLabel:
            product.bestOfferVariant?.offers &&
            product.bestOfferVariant?.offers[0]?.shortDisplayTitle,
          quantity: product.bestOfferVariant?.quantity,
          regularPrice: product.bestOfferVariant?.price,
          slug: product.slug,
          subcategoryName: product.subcategoryName,
          thcContent: product.thcContent,
          thcContentLabel: product.thcContentLabel,
          unit: product.bestOfferVariant?.unit,
        } as ProductCardProduct;

        return (
          <TrackImpression
            category={GTM_EVENTS.PRODUCT.CARD_IMPRESSION.category}
            className="h-full"
            customDimensions={dimensions}
            key={product.slug}
            label={eventLabel}
          >
            <ProductCard
              data-testid="product-card"
              product={productCard}
              onClick={() => {
                if (productCardOnClick) {
                  productCardOnClick();
                } else {
                  publishEvent({
                    ...GTM_EVENTS.PRODUCT.CARD_CLICK,
                    label: eventLabel,
                    ...dimensions,
                  });
                }
              }}
              className="h-full"
            />
          </TrackImpression>
        );
      })}
    </Carousel>
  );
};
